// @flow
import {useEffect, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useActions} from '../../../../../utils/globalStore';
import {actions} from '../duck';
import type {RootState} from '../../../..';
import type {
  ShippingMethodActive,
  ShippingFlatRateRequest,
  ShippingAdvancedRateRequest,
  ShippingEditAdvancedRateRequest,
  ShippingEditStandardRateRequest,
  ShippingStandardRateRequest,
} from '../../../../../entities';

const enabledLogisticShippingSelector = (state: RootState) =>
  state.logisticShippingConfig.shippingMethods;
const availableShippingCountriesSelector = (state: RootState) =>
  state.logisticShippingConfig.availableShippingCountries;

// SHIPPING GENERAL
export function useGetShippingMethodActive() {
  const shippingMethodActive = useSelector(enabledLogisticShippingSelector);

  return {
    shippingMethodActive,
  };
}

export function useFetchShippingMethods() {
  const shippingMethods = useSelector(enabledLogisticShippingSelector);
  const availableShippingCountries = useSelector(
    availableShippingCountriesSelector
  );
  const fetchEnabledLogisticShipping = useActions(
    actions.getEnabledShippingMethods
  );

  const fetchShippingMethods = useCallback(() => {
    return fetchEnabledLogisticShipping();
  }, [fetchEnabledLogisticShipping]);

  useEffect(() => {
    fetchShippingMethods();
  }, [fetchShippingMethods]);

  return {
    fetchShippingMethods,
    availableShippingCountries,
    shippingMethods,
  };
}

export function useUpdateEnabledShippingMethods() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const fetchEnabledLogisticShipping = useActions(
    actions.updateEnabledShippingMethods
  );
  const updateEnabledShippingMethods = useCallback(
    (enabled: boolean) => {
      return fetchEnabledLogisticShipping({enabled});
    },
    [fetchEnabledLogisticShipping]
  );

  return {
    isSaving,
    savingSuccess,
    savingError,
    updateEnabledShippingMethods,
  };
}

export function useUpdateShippingCountries() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingCountriesSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingCountriesError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const saveShippingCountries = useActions(actions.saveShippingCountries);

  const updateShippingCountries = (shippingCountries: string[]) => {
    return saveShippingCountries({shippingCountries});
  };

  return {
    isSaving,
    savingCountriesSuccess,
    savingCountriesError,
    updateShippingCountries,
  };
}

export function useUpdateShippingMethodActive() {
  const messageError = useSelector(
    (state: RootState) => state.logisticShippingConfig.messageError
  );
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingMethodSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingMethodError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const saveEnabledLogisticShipping = useActions(
    actions.saveShippingMethodActive
  );

  const updateShippingMethodActive = (
    shippingMethodActive: ShippingMethodActive
  ) => {
    return saveEnabledLogisticShipping({shippingMethodActive});
  };

  return {
    messageError,
    isSaving,
    savingMethodSuccess,
    savingMethodError,
    updateShippingMethodActive,
  };
}

// RESET
export function useResetState() {
  const resetStateAction = useActions(actions.resetState);

  const resetState = useCallback(() => {
    return resetStateAction();
  }, [resetStateAction]);

  return {
    resetState,
  };
}

// CURRENCY
const currencyCountry = (state: RootState) => state.currency.currency;

export function useGetCurrencyCountry() {
  return useSelector(currencyCountry);
}

// PaymentMethods
const paymentMethods = (state: RootState) =>
  state.paymentMethodConfig.paymentMethods;

export const usePaymentMethods = () => useSelector(paymentMethods);

// DATA
const dataShippingEstandar = (state: RootState) =>
  state.logisticShippingConfig.shippingDataEstandar;

export function useDataShippingEstandar() {
  const shippingDataEstandar = useSelector(dataShippingEstandar);

  return {
    shippingDataEstandar,
  };
}

const dataShippingManual = (state: RootState) =>
  state.logisticShippingConfig.shippingDataManual;

export function useDataShippingManual() {
  const shippingDataManual = useSelector(dataShippingManual);

  return {
    shippingDataManual,
  };
}

const dataShippingAdvanced = (state: RootState) =>
  state.logisticShippingConfig.shippingDataAdvanced;

export function useDataShippingAdvanced() {
  const shippingDataAdvanced = useSelector(dataShippingAdvanced);

  return {
    shippingDataAdvanced,
  };
}

// FLAT RATE
const dataFlatRate = (state: RootState) =>
  state.logisticShippingConfig.flatRate;

export function useDataFlatRate() {
  const flatRate = useSelector(dataFlatRate);
  const getFlatRate = useActions(actions.fetchFlatRate);

  const fetchFlatRate = useCallback(
    (flatRateId: string) => {
      return getFlatRate({flatRateId});
    },
    [getFlatRate]
  );

  return {
    flatRate,
    fetchFlatRate,
  };
}

export function useCRUDFlatRate() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingFlatRateSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingFlatRateError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const messageError = useSelector(
    (state: RootState) => state.logisticShippingConfig.messageError
  );
  const editFlatRate = useActions(actions.editFlatRate);
  const saveFlatRate = useActions(actions.saveFlatRate);
  const deleteFlatRate = useActions(actions.deleteFlatRate);

  const updateFlatRate = (flatRate: ShippingFlatRateRequest) => {
    return editFlatRate(flatRate);
  };

  const createFlatRate = (flatRateRequest: ShippingFlatRateRequest) => {
    return saveFlatRate({flatRate: flatRateRequest.flatRate});
  };

  const removeFlatRate = (flatRateId: string) => {
    return deleteFlatRate({flatRateId});
  };

  return {
    messageError,
    isSaving,
    savingFlatRateSuccess,
    savingFlatRateError,
    updateFlatRate,
    createFlatRate,
    removeFlatRate,
  };
}

// SHIPPING ADVANCED
const dataLocationsAdvanced = (state: RootState) =>
  state.logisticShippingConfig.locationsSelected;

export function useSaveLocationsSelected() {
  const shippinglocationsSelected = useSelector(dataLocationsAdvanced);
  const updateLocationsSelected = useActions(actions.saveLocationsSelected);

  const updateLocationsSelectedData = useCallback(
    (locationsSelected: any) => {
      return updateLocationsSelected({locationsSelected});
    },
    [updateLocationsSelected]
  );

  return {
    shippinglocationsSelected,
    updateLocationsSelectedData,
  };
}

const dataAdvancedRate = (state: RootState) =>
  state.logisticShippingConfig.advancedRate;

export function useFetchShippingAdvanced() {
  const shippingAdvanced = useSelector(dataAdvancedRate);
  const fetchShippingAdvanced = useActions(actions.fetchShippingAdvanced);

  const fetchShippingAdvancedData = useCallback(
    (advancedRateId: string) => {
      return fetchShippingAdvanced({advancedRateId});
    },
    [fetchShippingAdvanced]
  );

  return {
    shippingAdvanced,
    fetchShippingAdvancedData,
  };
}

export function useCRUDAdvancedRate() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingAdvancedRateSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingAdvancedRateError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const editAdvancedRate = useActions(actions.editShippingAdvanced);
  const saveAdvancedRate = useActions(actions.saveShippingAdvanced);
  const deleteAdvancedRate = useActions(actions.deleteShippingAdvanced);

  const updateAdvancedRate = (
    advancedRate: ShippingEditAdvancedRateRequest
  ) => {
    return editAdvancedRate(advancedRate);
  };

  const createAdvancedRate = (
    advancedRateRequest: ShippingAdvancedRateRequest
  ) => {
    return saveAdvancedRate({advancedRate: advancedRateRequest});
  };

  const removeAdvancedRate = (advancedRateId: string) => {
    return deleteAdvancedRate({advancedRateId});
  };

  return {
    isSaving,
    savingAdvancedRateSuccess,
    savingAdvancedRateError,
    updateAdvancedRate,
    createAdvancedRate,
    removeAdvancedRate,
  };
}

export function useAllowOrdersOutsideDeliveryZone() {
  const updateAllowOrdersOutsideDeliveryZoneAction = useActions(
    actions.updateAllowOrderOutsideDeliveryZone
  );

  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );

  const savingShippingSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );

  const updateAllowOrdersOutsideDeliveryZone = (allowOrdersOutsideDeliveryZone: {
    storeShippingMethodAdvancedSetting: {
      enabledAdminOutsideShipping: boolean,
    },
  }) => {
    return updateAllowOrdersOutsideDeliveryZoneAction({
      allowOrdersOutsideDeliveryZone,
    });
  };

  return {
    isSaving,
    savingShippingSuccess,
    updateAllowOrdersOutsideDeliveryZone,
  };
}

export function useClearAllLocationsSelected() {
  const clearAllLocationsSelectedAction = useActions(
    actions.clearAllLocationsSelected
  );

  const clearAllLocationsSelected = (countryCode: string) => {
    return clearAllLocationsSelectedAction({countryCode});
  };

  return {
    clearAllLocationsSelected,
  };
}

// SHIPPING STANDARD
const dataStandardRate = (state: RootState) =>
  state.logisticShippingConfig.standardRate;

export function useFetchShippingStandard() {
  const shippingStandard = useSelector(dataStandardRate);
  const fetchShippingStandard = useActions(actions.fetchShippingStandard);

  const fetchShippingStandardData = useCallback(
    (standardRateId: string) => {
      return fetchShippingStandard({standardRateId});
    },
    [fetchShippingStandard]
  );

  return {
    shippingStandard,
    fetchShippingStandardData,
  };
}

export function useCRUDStandardRate() {
  const isSaving = useSelector(
    (state: RootState) => state.logisticShippingConfig.isSaving
  );
  const savingStandardRateSuccess = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingSuccess
  );
  const savingStandardRateError = useSelector(
    (state: RootState) => state.logisticShippingConfig.savingShippingError
  );
  const editStandardRate = useActions(actions.editShippingStandard);
  const saveStandardRate = useActions(actions.saveShippingStandard);
  const deleteStandardRate = useActions(actions.deleteShippingStandard);

  const updateStandardRate = (
    standardRate: ShippingEditStandardRateRequest
  ) => {
    return editStandardRate(standardRate);
  };

  const createStandardRate = (
    standardRateRequest: ShippingStandardRateRequest
  ) => {
    return saveStandardRate({standardRate: standardRateRequest});
  };

  const removeStandardRate = (standardRateId: string) => {
    return deleteStandardRate({standardRateId});
  };

  return {
    isSaving,
    savingStandardRateSuccess,
    savingStandardRateError,
    updateStandardRate,
    createStandardRate,
    removeStandardRate,
  };
}
